import { CueType } from '~shared/rundown/cue'
import type { Cue } from '~shared/rundown/cue'
import type { ReadonlyRundownCueOrderItem, Rundown, RundownCueOrderItem } from '~shared/rundown/rundown'

import { getIndexWithPrefix } from './getIndexWithPrefix'

/**
 * Look up a cue document by id.
 *
 * Cue helpers take the collection as a lookup function rather than a `Record`
 * or a `Map`, so the same code serves a Firestore-backed dictionary, a store
 * keyed by id, or a plain object built in a test.
 *
 * Returns `undefined` when the cue order names a cue whose document is not
 * available — order and documents are stored separately, so this is a normal
 * transient state, not an error.
 */
export type GetCue = (cueId: string) => Cue | undefined

/** The subset of `Rundown.settings` that drives cue index labels. */
export type CueIndexSettings = Pick<Rundown['settings'], 'cueIndexStartFrom' | 'cueIndexPrefix' | 'cueIndexPadding'>

/**
 * A cue paired with its position in the rundown.
 *
 * Position and label only, no cue content: a consumer that needs the title
 * looks the cue up, which is what lets a UI render the label without
 * subscribing to the cue itself.
 */
export interface IndexedCue {
  id: string
  type: Cue['type']
  /** Position in the flattened list, groups and headings included. */
  index: number
  /** Label shown to the user, e.g. `A007`, or `A007.2` for a group child. */
  displayIndex: string
  /** Set only on children of a group. */
  parentId?: string
  /** True for the last child of a group. Drives the tree-line connector. */
  lastSibling: boolean
  /** Set only on group cues. */
  children?: RundownCueOrderItem[]
}

/**
 * Walk the cue order and attach display indices.
 *
 * The numbering rules, which are the whole reason this is not a plain `map`:
 *
 * - Headings do not consume an index, they are labels, not items.
 * - Groups consume an index, and their children are numbered beneath it (`A007` → `A007.1`, `A007.2`).
 * - Child numbering restarts inside every group.
 * - A cue named by the order whose document is missing is skipped without consuming an index, so labels do not shift once
 *   it loads.
 *
 * @remarks
 *   Every surface that numbers cues for a human — the editor, the outputs, the
 *   exports — has to agree, or the same cue is "7" on screen and "8" in the
 *   PDF someone printed. That is why this is shared rather than living in the
 *   client.
 */
export function buildIndexedCues(
  cueOrder: readonly ReadonlyRundownCueOrderItem[],
  getCue: GetCue,
  settings: CueIndexSettings,
): IndexedCue[] {
  const { cueIndexStartFrom, cueIndexPrefix, cueIndexPadding } = settings

  const result: IndexedCue[] = []
  let displayIndex = cueIndexStartFrom - 1
  let flatIndex = 0

  for (const item of cueOrder) {
    const cue = getCue(item.id)
    if (!cue) continue

    if (cue.type !== CueType.HEADING) displayIndex += 1

    const label = getIndexWithPrefix(displayIndex, cueIndexPrefix, cueIndexPadding)

    const indexedCue: IndexedCue = {
      id: item.id,
      type: cue.type,
      index: flatIndex,
      displayIndex: label,
      lastSibling: false,
    }
    flatIndex += 1

    const isGroup = cue.type === CueType.GROUP && Array.isArray(item.children)
    if (!isGroup) {
      result.push(indexedCue)
      continue
    }

    const children = item.children ?? []
    indexedCue.children = children
    result.push(indexedCue)

    let childDisplayIndex = 0
    for (let i = 0; i < children.length; i++) {
      const child = children[i]
      if (!child) continue

      const childCue = getCue(child.id)
      if (!childCue) continue

      if (childCue.type !== CueType.HEADING) childDisplayIndex += 1

      result.push({
        id: child.id,
        type: childCue.type,
        index: flatIndex,
        displayIndex: `${label}.${childDisplayIndex}`,
        parentId: item.id,
        lastSibling: i + 1 === children.length,
      })
      flatIndex += 1
    }
  }

  return result
}
